import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash, Search, X, Layers, RefreshCw, Calendar, Info, Clock, DollarSign, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, Package, ArrowRight } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const PhuPhamManagement = () => {
    // State Management
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Modal & Form States
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        'ID_PP': '',
        'TEN_HANG': '',
        'HIEULUC_TU': '',
        'HIEULUC_DEN': '',
        'DONGIA': ''
    });

    // Delete Confirmation State
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    // Filter State
    const [filterType, setFilterType] = useState('ALL'); // ALL, ACTIVE, UPCOMING, EXPIRED

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Fetch data
    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await authUtils.apiRequest('PHU_PHAM', 'Find', {});
            // Ensure response is an array
            const data = Array.isArray(response) ? response : [];
            setItems(data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Lỗi khi tải danh sách phụ phẩm');
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Statistics Calculation
    const stats = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        let total = items.length;
        let active = 0;
        let expired = 0;
        let upcoming = 0;

        items.forEach(item => {
            const start = item['HIEULUC_TU'] ? new Date(item['HIEULUC_TU']) : null;
            const end = item['HIEULUC_DEN'] ? new Date(item['HIEULUC_DEN']) : null;

            if (start && end) {
                if (now > end) {
                    expired++;
                } else if (now < start) {
                    upcoming++;
                } else {
                    active++;
                }
            }
        });

        return { total, active, expired, upcoming };
    }, [items]);

    // Helper: Format Date for Display
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('vi-VN');
        } catch {
            return dateString;
        }
    };

    // Helper: Format Date for Input (YYYY-MM-DD)
    const formatDateInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    // Helper: Format Date to DD/MM/YYYY
    const formatDateToDDMMYYYY = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return '';
        }
    };

    // Helper: Generate ID_PP
    // Format: TEN_HANG(DD/MM/YYYY-DD/MM/YYYY)
    const generateIdPP = (tenHang, tuNgay, denNgay) => {
        if (!tenHang || !tuNgay || !denNgay) return '';
        const fmtTuNgay = formatDateToDDMMYYYY(tuNgay);
        const fmtDenNgay = formatDateToDDMMYYYY(denNgay);
        return `${tenHang.toUpperCase()}(${fmtTuNgay}-${fmtDenNgay})`;
    };

    // Auto update ID when fields change (only in regular mode, or if we want to show it in edit mode)
    useEffect(() => {
        const newId = generateIdPP(
            currentItem.TEN_HANG,
            currentItem.HIEULUC_TU,
            currentItem.HIEULUC_DEN
        );

        if (newId !== currentItem.ID_PP) {
            setCurrentItem(prev => ({ ...prev, ID_PP: newId }));
        }
    }, [currentItem.TEN_HANG, currentItem.HIEULUC_TU, currentItem.HIEULUC_DEN]);

    // Handle Form Input Change
    const handleInputChange = (field, value) => {
        setCurrentItem(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Modal Handlers
    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEditMode(true);
            setCurrentItem({
                ...item,
                'HIEULUC_TU': formatDateInput(item['HIEULUC_TU']),
                'HIEULUC_DEN': formatDateInput(item['HIEULUC_DEN'])
            });
        } else {
            setIsEditMode(false);
            setCurrentItem({
                'ID_PP': '',
                'TEN_HANG': '',
                'HIEULUC_TU': '',
                'HIEULUC_DEN': '',
                'DONGIA': ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setIsSubmitting(false);
    };

    // Validation
    const validateForm = () => {
        if (!currentItem.TEN_HANG) return 'Vui lòng nhập tên hàng';
        if (!currentItem.HIEULUC_TU) return 'Vui lòng chọn ngày hiệu lực từ';
        if (!currentItem.HIEULUC_DEN) return 'Vui lòng chọn ngày hiệu lực đến';
        if (!currentItem.DONGIA) return 'Vui lòng nhập đơn giá';
        return null;
    };

    // Save Data
    const handleSave = async () => {
        const error = validateForm();
        if (error) {
            toast.error(error);
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const dataToSave = {
                'ID_PP': currentItem.ID_PP,
                'TEN_HANG': currentItem.TEN_HANG,
                'HIEULUC_TU': currentItem.HIEULUC_TU,
                'HIEULUC_DEN': currentItem.HIEULUC_DEN,
                'DONGIA': currentItem.DONGIA
            };

            if (isEditMode) {
                await authUtils.apiRequest('PHU_PHAM', 'Edit', {
                    "Rows": [dataToSave]
                });
                toast.success('Cập nhật thành công!');
            } else {
                await authUtils.apiRequest('PHU_PHAM', 'Add', {
                    "Rows": [dataToSave]
                });
                toast.success('Thêm mới thành công!');
            }

            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Có lỗi xảy ra khi lưu dữ liệu');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete Handlers
    const handleOpenDeleteConfirmation = (item) => {
        setItemToDelete(item);
        setShowDeleteConfirmation(true);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await authUtils.apiRequest('PHU_PHAM', 'Delete', {
                "Rows": [{ "ID_PP": itemToDelete['ID_PP'] }]
            });
            toast.success('Xóa thành công!');
            fetchData();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Có lỗi xảy ra khi xóa');
        } finally {
            setShowDeleteConfirmation(false);
            setItemToDelete(null);
        }
    };

    // Sorting
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedItems = useMemo(() => {
        let sortableItems = [...items];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [items, sortConfig]);

    // Filtering
    const filteredItems = sortedItems.filter(item => {
        const term = search.toLowerCase();
        const matchesSearch = (
            (item['TEN_HANG']?.toLowerCase().includes(term)) ||
            (item['ID_PP']?.toLowerCase().includes(term))
        );

        if (!matchesSearch) return false;

        if (filterType === 'ALL') return true;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const start = item['HIEULUC_TU'] ? new Date(item['HIEULUC_TU']) : null;
        const end = item['HIEULUC_DEN'] ? new Date(item['HIEULUC_DEN']) : null;

        if (!start || !end) return false;

        if (filterType === 'ACTIVE') {
            return now >= start && now <= end;
        }
        if (filterType === 'UPCOMING') {
            return now < start;
        }
        if (filterType === 'EXPIRED') {
            return now > end;
        }

        return true;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    // Pagination Utils
    const goToPage = (page) => setCurrentPage(page);
    const getPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) pages.push(i);
        return pages; // Simple version, can be optimized for many pages
    };

    // Formatter for Currency
    const formatCurrency = (value) => {
        if (!value) return '0';
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    };

    return (
        <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-100">
                    {/* Header */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
                                <Layers className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-800">Quản Lý Phụ Phẩm</h1>
                                <p className="text-xs text-gray-500 mt-0.5">Quản lý danh sách và giá các loại phụ phẩm</p>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={fetchData}
                                className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1.5 transition-all shadow-sm text-sm"
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span className="hidden sm:inline">Làm mới</span>
                            </button>
                            <button
                                onClick={() => handleOpenModal()}
                                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm text-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm mới
                            </button>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {/* Card 1: Total */}
                        <div
                            onClick={() => setFilterType('ALL')}
                            className={`bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-105 ${filterType === 'ALL' ? 'ring-4 ring-blue-300 ring-offset-2' : 'opacity-90 hover:opacity-100'}`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium">Tổng số phụ phẩm</p>
                                    <h3 className="text-3xl font-bold mt-1">{stats.total}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Package className="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Card 2: Active */}
                        <div
                            onClick={() => setFilterType('ACTIVE')}
                            className={`bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-105 ${filterType === 'ACTIVE' ? 'ring-4 ring-green-300 ring-offset-2' : 'opacity-90 hover:opacity-100'}`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-green-100 text-sm font-medium">Đang hiệu lực</p>
                                    <h3 className="text-3xl font-bold mt-1">{stats.active}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <CheckCircle className="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Upcoming */}
                        <div
                            onClick={() => setFilterType('UPCOMING')}
                            className={`bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-105 ${filterType === 'UPCOMING' ? 'ring-4 ring-yellow-300 ring-offset-2' : 'opacity-90 hover:opacity-100'}`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-yellow-100 text-sm font-medium">Sắp hiệu lực</p>
                                    <h3 className="text-3xl font-bold mt-1">{stats.upcoming}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Clock className="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Card 4: Expired */}
                        <div
                            onClick={() => setFilterType('EXPIRED')}
                            className={`bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg cursor-pointer transition-all transform hover:scale-105 ${filterType === 'EXPIRED' ? 'ring-4 ring-red-300 ring-offset-2' : 'opacity-90 hover:opacity-100'}`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-red-100 text-sm font-medium">Hết hiệu lực</p>
                                    <h3 className="text-3xl font-bold mt-1">{stats.expired}</h3>
                                </div>
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <AlertCircle className="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="mb-4 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Tìm kiếm theo tên hàng, mã phụ phẩm..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('ID_PP')}>
                                        Mã Phụ Phẩm {sortConfig.key === 'ID_PP' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('TEN_HANG')}>
                                        Tên Hàng {sortConfig.key === 'TEN_HANG' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('HIEULUC_TU')}>
                                        Hiệu Lực Từ {sortConfig.key === 'HIEULUC_TU' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('HIEULUC_DEN')}>
                                        Hiệu Lực Đến {sortConfig.key === 'HIEULUC_DEN' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('DONGIA')}>
                                        Đơn Giá {sortConfig.key === 'DONGIA' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Thao tác
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                                            Đang tải dữ liệu...
                                        </td>
                                    </tr>
                                ) : currentItems.length > 0 ? (
                                    currentItems.map((item, index) => (
                                        <tr key={index} className="hover:bg-blue-50 transition-colors">
                                            <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                                                {item['ID_PP']}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700">
                                                {item['TEN_HANG']}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600 text-center">
                                                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                                    {formatDateDisplay(item['HIEULUC_TU'])}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600 text-center">
                                                <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                                    {formatDateDisplay(item['HIEULUC_DEN'])}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-sm font-semibold text-blue-600 text-right">
                                                {formatCurrency(item['DONGIA'])}
                                            </td>
                                            <td className="px-4 py-2 text-center text-sm">
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => handleOpenModal(item)}
                                                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                                        title="Chỉnh sửa"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteConfirmation(item)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                        title="Xóa"
                                                    >
                                                        <Trash className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            Không có dữ liệu
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {filteredItems.length > 0 && (
                        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="text-xs text-gray-600">
                                Trang <span className="font-semibold text-gray-800">{currentPage}</span> / <span className="font-semibold text-gray-800">{totalPages}</span>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => goToPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="p-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                {getPageNumbers().map(page => (
                                    <button
                                        key={page}
                                        onClick={() => goToPage(page)}
                                        className={`px-3 py-1 text-xs border rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-50'}`}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 border rounded hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditMode ? 'Cập Nhật Phụ Phẩm' : 'Thêm Phụ Phẩm Mới'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tên Hàng <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={currentItem.TEN_HANG}
                                    onChange={(e) => handleInputChange('TEN_HANG', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Nhập tên phụ phẩm"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hiệu Lực Từ <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                                        <input
                                            type="date"
                                            value={currentItem.HIEULUC_TU}
                                            onChange={(e) => handleInputChange('HIEULUC_TU', e.target.value)}
                                            className="w-full pl-9 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Hiệu Lực Đến <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                                        <input
                                            type="date"
                                            value={currentItem.HIEULUC_DEN}
                                            onChange={(e) => handleInputChange('HIEULUC_DEN', e.target.value)}
                                            className="w-full pl-9 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Đơn Giá <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <DollarSign className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                                    <input
                                        type="number"
                                        value={currentItem.DONGIA}
                                        onChange={(e) => handleInputChange('DONGIA', e.target.value)}
                                        className="w-full pl-9 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Mã Phụ Phẩm (Tự động)</label>
                                <div className="font-mono text-sm text-gray-800 break-all">
                                    {currentItem.ID_PP || '---'}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                            >
                                Hủy bỏ
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
                            >
                                {isSubmitting && <RefreshCw className="w-3 h-3 animate-spin" />}
                                {isEditMode ? 'Cập nhật' : 'Thêm mới'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-5 animate-fadeIn">
                        <div className="flex flex-col items-center text-center">
                            <div className="p-3 bg-red-100 rounded-full mb-3">
                                <Trash className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Xác nhận xóa</h3>
                            <p className="text-gray-500 text-sm mb-4">
                                Bạn có chắc chắn muốn xóa phụ phẩm <br />
                                <span className="font-semibold text-gray-800">{itemToDelete?.TEN_HANG}</span>?
                            </p>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setShowDeleteConfirmation(false)}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                                >
                                    Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
            `}</style>
        </div>
    );
};

export default PhuPhamManagement;
